import { supabase } from '@/core/database/supabase';
import type { 
  MaintenanceRoute, 
  RouteStop, 
  RouteTask, 
  RouteAssignment, 
  RouteLog, 
  RouteStats, 
  RouteFilters 
} from '../types/routes.types';

export const MaintenanceRoutesService = {
  /**
   * Lấy danh sách tuyến bảo dưỡng
   */
  async getRoutes(companyId: string, filters: RouteFilters = {}): Promise<{ data: MaintenanceRoute[], total: number }> {
    let query = supabase
      .from('maintenance_routes')
      .select(`
        *,
        project:projects(id, name),
        team:teams(id, name),
        assignee:user_profiles!assigned_to(id, full_name, avatar_url:avatar_path)
      `, { count: 'exact' })
      .eq('company_id', companyId);

    if (filters.status && filters.status !== 'all') query = query.eq('status_code', filters.status);
    if (filters.projectId) query = query.eq('project_id', filters.projectId);
    if (filters.teamId) query = query.eq('team_id', filters.teamId);
    if (filters.search) {
      query = query.or(`name.ilike.%${filters.search}%,code.ilike.%${filters.search}%`);
    }

    query = query.order('name', { ascending: true });

    const { data, error, count } = await query;

    if (error) throw error;
    return {
      data: (data || []) as MaintenanceRoute[],
      total: count || 0
    };
  },

  /**
   * Chi tiết tuyến + các điểm dừng
   */
  async getRouteDetail(id: string): Promise<MaintenanceRoute> {
    const { data, error } = await supabase
      .from('maintenance_routes')
      .select(`
        *,
        project:projects(id, name),
        team:teams(id, name),
        assignee:user_profiles!assigned_to(id, full_name, avatar_url:avatar_path)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as MaintenanceRoute;
  },

  /**
   * Lấy danh sách điểm dừng của tuyến
   */
  async getRouteStops(routeId: string): Promise<RouteStop[]> {
    const { data, error } = await supabase
      .from('maintenance_route_stops')
      .select(`
        *,
        zone:project_zones(id, zone_name),
        workload:maintenance_workloads(id, name),
        tasks:maintenance_route_tasks(*)
      `)
      .eq('route_id', routeId)
      .order('sequence_order', { ascending: true });

    if (error) throw error;
    return data as RouteStop[];
  },

  /**
   * Thống kê tuyến
   */
  async getRouteStats(companyId: string): Promise<RouteStats> {
    const { data: routes } = await supabase.from('maintenance_routes').select('status_code').eq('company_id', companyId);
    const { data: assigns } = await supabase.from('maintenance_route_assignments').select('status').eq('scheduled_date', new Date().toISOString().split('T')[0]);

    return {
      totalRoutes: routes?.length || 0,
      activeRoutes: routes?.filter(r => r.status_code === 'active').length || 0,
      pendingAssignments: assigns?.filter(a => a.status === 'pending').length || 0,
      completedToday: assigns?.filter(a => a.status === 'completed').length || 0,
    };
  },

  /**
   * Nhật ký tuyến
   */
  async getRouteLogs(assignmentId: string): Promise<RouteLog[]> {
    const { data, error } = await supabase
      .from('maintenance_route_logs')
      .select('*')
      .eq('route_assignment_id', assignmentId)
      .order('performed_at', { ascending: false });

    if (error) throw error;
    return data as RouteLog[];
  },

  /**
   * CRUD Operations
   */
  async createRoute(data: Partial<MaintenanceRoute>): Promise<MaintenanceRoute> {
    const { data: result, error } = await supabase
      .from('maintenance_routes')
      .insert([data])
      .select()
      .single();

    if (error) throw error;
    return result as MaintenanceRoute;
  },

  async updateRoute(id: string, updates: Partial<MaintenanceRoute>): Promise<MaintenanceRoute> {
    const { data, error } = await supabase
      .from('maintenance_routes')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as MaintenanceRoute;
  }
};

